import { createBrowserHistory, createMemoryHistory } from 'history';

// Native WebView assessments handoff. When window.TLM.isNative, list/info/scores
// are owned by native: abort those navigations, unmount those trees, skip their fetches.
export const NATIVE_ASSESSMENT_PAGES = ['info', 'scores'];
export const APP_ACTIONS_KEY = 'APP_ACTIONS';

const canUseDom = () => typeof window !== 'undefined' && !!(window.history && window.history.pushState);

const nativeWindow = () => (typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : {});

export const history = canUseDom() ? createBrowserHistory() : createMemoryHistory();

let nativeReportsVisible = false;
let hashViewBlocksHandoff = false;
let initialized = false;
let historyWrapped = false;
let pushRealmToNative = null;
let pendingReadyPath = null;

export const isNativeShell = () => {
  const tlm = nativeWindow().TLM;
  return !!(tlm && tlm.isNative);
};

export function setHashViewBlocksHandoff(active) {
  hashViewBlocksHandoff = !!active;
}

export function getNativeReportsVisible() {
  const tlm = nativeWindow().TLM;
  if (tlm && typeof tlm.reportsVisible === 'boolean') {
    return !!tlm.reportsVisible;
  }
  return nativeReportsVisible;
}

export function setNativeReportsVisible(value) {
  nativeReportsVisible = !!value;
}

/** NavRealm registers this so native can request the live picker realm (not persist:root.auth). */
export function setPushRealmToNative(fn) {
  pushRealmToNative = typeof fn === 'function' ? fn : null;
}

export function resolveAppPath(path, currentPath) {
  const current = (currentPath || (canUseDom() ? window.location.pathname : history.location.pathname) || '/').split('?')[0].split('#')[0] || '/';
  if (path == null || path === '') return current;

  let pathname = path;
  if (typeof path === 'object') {
    pathname = path.pathname || current;
  } else {
    const raw = String(path);
    const q = raw.indexOf('?');
    const h = raw.indexOf('#');
    const cut = [q, h].filter((i) => i >= 0).sort((a, b) => a - b)[0];
    pathname = cut === undefined ? raw : raw.slice(0, cut);
  }

  if (!pathname) return current;
  if (pathname.startsWith('/')) return pathname;

  const base = current.endsWith('/') ? current : `${current.replace(/\/[^/]*$/, '')}/`;
  try {
    return new URL(pathname, `https://native.local${base || '/'}`).pathname;
  } catch (err) {
    return `/${pathname}`;
  }
}

export function nativeReportsHandoff(path) {
  const p = (resolveAppPath(path) || '').split('?')[0].replace(/\/+$/, '') || '/';
  if (p === '/reports' || p === '/reports/list' || p.endsWith('/reports/list')) return true;
  const m = p.match(/\/reports\/assessment\/([^/]+)\/([^/]+)$/);
  if (m && NATIVE_ASSESSMENT_PAGES.includes(m[2])) {
    return { id: m[1], page: m[2] };
  }
  const bare = p.match(/\/reports\/assessment\/([^/]+)$/);
  if (bare) {
    return { id: bare[1], page: 'scores' };
  }
  return null;
}

export function handOffToNativeIfNeeded(path) {
  if (!isNativeShell() || hashViewBlocksHandoff) return false;
  const payload = nativeReportsHandoff(path);
  if (payload == null) return false;
  const tlm = nativeWindow().TLM;
  try {
    if (tlm && typeof tlm.openNativeReports === 'function') {
      tlm.openNativeReports(payload);
    }
  } catch (err) {
    console.warn(err);
  }
  return true;
}

export function shouldUnmountNativeOwnedReports(path) {
  if (!isNativeShell() || hashViewBlocksHandoff) return false;
  return nativeReportsHandoff(path) != null;
}

export function shouldSkipNativeReportsFetch(url = '') {
  if (!isNativeShell() || hashViewBlocksHandoff) return false;
  const path = canUseDom() ? window.location.pathname : history.location.pathname;
  if (!getNativeReportsVisible() && nativeReportsHandoff(path) == null) return false;

  const raw = String(url || '').split('?')[0].replace(/\/+$/, '');
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (normalized === '/assessments' || normalized === '/search/assessments') return true;
  if (/^\/assessments\/[^/]+$/.test(normalized)) return true;
  if (normalized === '/processed-scorecard-score-values' || normalized.startsWith('/processed-scorecard-score-values/')) return true;
  return false;
}

function pathsMatch(a, b) {
  const pa = resolveAppPath(a);
  const pb = resolveAppPath(b);
  return pa === pb || pa.endsWith(pb) || pb.endsWith(pa);
}

function sendWebNavigated(path) {
  const tlm = nativeWindow().TLM;
  try {
    if (tlm && typeof tlm.sendEvent === 'function') {
      tlm.sendEvent({ action: 'webNavigated', data: { path } });
      return;
    }
  } catch (err) {
    console.warn(err);
  }
  try {
    const wv = nativeWindow().ReactNativeWebView;
    if (wv && typeof wv.postMessage === 'function') {
      wv.postMessage(JSON.stringify({ action: 'webNavigated', data: { path } }));
    }
  } catch (err) {
    console.warn(err);
  }
}

/** After the destination route has committed (and preferably painted). */
export function notifyWebReadyIfNeeded(pathname) {
  if (!pendingReadyPath) return false;
  if (!pathsMatch(pathname, pendingReadyPath)) return false;
  const path = pendingReadyPath;
  pendingReadyPath = null;
  sendWebNavigated(path);
  return true;
}

function isUnauthedAppPath(path) {
  const p = resolveAppPath(path);
  return p === '/' || p === '/logout' || p.indexOf('/logout') === 0
    || p.indexOf('/register') === 0 || p.indexOf('/policy') === 0;
}

let pendingLoginUnlisten = null;

function clearPendingLoginNav() {
  if (typeof pendingLoginUnlisten === 'function') {
    pendingLoginUnlisten();
  }
  pendingLoginUnlisten = null;
}

function navigateFromNative(path) {
  if (handOffToNativeIfNeeded(path)) return;
  const dest = resolveAppPath(path);
  pendingReadyPath = dest;
  const current = resolveAppPath(history.location.pathname);

  // Login (`/`) is still mounted until theme + account settings land.
  // Pushing Data/Photos/Tasks onto that document mounts Nav/Droprealms
  // without a session and 401s into /logout.
  if (isUnauthedAppPath(current) && !isUnauthedAppPath(dest)) {
    clearPendingLoginNav();
    pendingLoginUnlisten = history.listen((location) => {
      if (isUnauthedAppPath(location.pathname)) return;
      clearPendingLoginNav();
      navigateFromNative(path);
    });
    return;
  }

  if (pathsMatch(current, dest)) {
    // Already on dest — NativeWebReady will not re-fire. Signal after this frame.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => notifyWebReadyIfNeeded(history.location.pathname));
    } else {
      notifyWebReadyIfNeeded(history.location.pathname);
    }
    return;
  }
  history.push(path);
}

function wrapHistory(h) {
  if (historyWrapped || !h) return;
  historyWrapped = true;
  const origPush = h.push.bind(h);
  const origReplace = h.replace.bind(h);
  h.push = (path, state) => {
    if (handOffToNativeIfNeeded(resolveAppPath(path, h.location.pathname))) return;
    return origPush(path, state);
  };
  h.replace = (path, state) => {
    if (handOffToNativeIfNeeded(resolveAppPath(path, h.location.pathname))) return;
    return origReplace(path, state);
  };
}

function onNativeShell(e) {
  const { reportsVisible, requestRealm } = (e && e.detail) || {};
  setNativeReportsVisible(!!reportsVisible);
  if (requestRealm && typeof pushRealmToNative === 'function') {
    try {
      pushRealmToNative();
    } catch (err) {
      console.warn(err);
    }
  }
}

function onDocumentClick(e) {
  if (!isNativeShell() || hashViewBlocksHandoff) return;
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const target = e.target;
  const a = target && target.closest ? target.closest('a[href]') : null;
  if (!a) return;
  if (a.target && a.target !== '_self') return;
  const href = a.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
  let path = href;
  try {
    if (/^https?:/i.test(href)) {
      const url = new URL(href);
      if (canUseDom() && url.origin !== window.location.origin) return;
      path = `${url.pathname}${url.search || ''}`;
    }
  } catch (err) {
    return;
  }
  if (handOffToNativeIfNeeded(path)) {
    e.preventDefault(); // do not stopPropagation — the nav drawer closes on this click
  }
}

export function initNativeShell() {
  if (initialized) return;
  initialized = true;
  wrapHistory(history);

  const w = nativeWindow();
  w[APP_ACTIONS_KEY] = w[APP_ACTIONS_KEY] || {};
  w[APP_ACTIONS_KEY].navigate = navigateFromNative;

  // Listen even before TLM exists. Module init can run before the native
  // injected script; requestRealm / reportsVisible events must not be missed.
  if (typeof w.addEventListener === 'function') {
    w.addEventListener('nativeShell', onNativeShell);
  }

  let clickWrapped = false;
  const attachNativeOnly = () => {
    if (!isNativeShell()) return false;
    if (typeof w.TLM.reportsVisible === 'boolean') {
      setNativeReportsVisible(!!w.TLM.reportsVisible);
    }
    if (canUseDom() && !clickWrapped) {
      clickWrapped = true;
      document.addEventListener('click', onDocumentClick, true);
      handOffToNativeIfNeeded(`${window.location.pathname}${window.location.search || ''}`);
    }
    return true;
  };

  if (attachNativeOnly()) return;
  if (typeof w.setInterval !== 'function') return;
  let n = 0;
  const t = w.setInterval(() => {
    n += 1;
    if (attachNativeOnly() || n > 20) w.clearInterval(t);
  }, 100);
}

export function __resetNativeShellForTests() {
  nativeReportsVisible = false;
  hashViewBlocksHandoff = false;
  pushRealmToNative = null;
  pendingReadyPath = null;
  if (typeof pendingLoginUnlisten === 'function') {
    pendingLoginUnlisten();
  }
  pendingLoginUnlisten = null;
}

initNativeShell();
